/** @file boneTails: where each bone ends: the children's mean, else the image reach along the parent bone. */
import type { PoseBuffer } from '@zilo/pose3d-pose/contract';
import { POSE_LAYOUT as L } from '@zilo/pose3d-pose/layout/poseLayout';

/** A leaf bone's length in units when no `leafLen` is given, at most its parent bone's. */
const STUB = 0.5;
/** Pose position columns by axis. */
const COLUMNS = [L.px, L.py, L.pz] as const;

/** Element `i` of a numeric array (0 past its end). */
const get = (a: ArrayLike<number>, i: number): number => a[i] ?? 0;

/** Node `i`'s position component `c` (0 x, 1 y, 2 z). */
const pos = (pose: PoseBuffer, i: number, c: 0 | 1 | 2): number => get(pose, i * L.stride + COLUMNS[c]);

/**
 * Writes node `i`'s tail `len` units along its parent bone (up for a root). With `leafLen` the length is the image
 * reach (`leafLengths`, 0 = no body, only the joint ring); without it the `STUB`, at most the parent bone's length.
 */
function stub(pose: PoseBuffer, i: number, out: Float64Array, leafLen: ArrayLike<number> | undefined): void {
  const p = get(pose, i * L.stride + L.parentIndex);
  const dx = p >= 0 ? pos(pose, i, 0) - pos(pose, p, 0) : 0;
  const dy = p >= 0 ? pos(pose, i, 1) - pos(pose, p, 1) : 0;
  const dz = p >= 0 ? pos(pose, i, 2) - pos(pose, p, 2) : 0;
  const n = Math.hypot(dx, dy, dz);
  const len = leafLen === undefined ? Math.min(STUB, n > 1e-6 ? n : STUB) : get(leafLen, i);
  const s = n > 1e-6 ? len / n : 0;
  out[3 * i] = pos(pose, i, 0) + dx * s;
  out[3 * i + 1] = pos(pose, i, 1) + dy * s;
  out[3 * i + 2] = pos(pose, i, 2) + (n > 1e-6 ? dz * s : len);
}

/** Adds every node's position to its parent's sum and counts the children. */
function sumChildren(pose: PoseBuffer, n: number, out: Float64Array, counts: Float64Array): void {
  for (let i = 0; i < n; i++) {
    const p = get(pose, i * L.stride + L.parentIndex);
    if (p < 0 || p >= n) continue;
    for (let c = 0; c < 3; c++) out[3 * p + c] = get(out, 3 * p + c) + pos(pose, i, c as 0 | 1 | 2);
    counts[p] = get(counts, p) + 1;
  }
}

/**
 * For nodes `0..n-1`: `out[3i..3i+2]` = the bone tail of node `i` (entity-relative world, z up): the mean of its
 * children's positions (pose `parentIndex`), or, when it has none or they sit on it, a tail along its parent bone of
 * `leafLen[i]` units (the drawn image's reach, `leafLengths`) or the `STUB`. `counts` (n) is scratch; nothing is
 * allocated, so the frame loop calls it with its own buffers.
 */
export function boneTails(pose: PoseBuffer, n: number, out: Float64Array, counts: Float64Array, leafLen?: ArrayLike<number>): Float64Array {
  out.fill(0, 0, 3 * n);
  counts.fill(0, 0, n);
  sumChildren(pose, n, out, counts);
  for (let i = 0; i < n; i++) {
    const k = get(counts, i);
    if (k > 0) for (let c = 0; c < 3; c++) out[3 * i + c] = get(out, 3 * i + c) / k;
    const len = Math.hypot(get(out, 3 * i) - pos(pose, i, 0), get(out, 3 * i + 1) - pos(pose, i, 1), get(out, 3 * i + 2) - pos(pose, i, 2));
    if (k === 0 || len < 1e-4) stub(pose, i, out, leafLen);
  }
  return out;
}
